import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import GuestBioModal from './GuestBioModal';

// Curated featured conversations. Bios kept short, factual, and grounded in
// what was mentioned in the actual episode descriptions.
const FEATURED = [
  {
    id: 'padilla',
    name: 'Col. Francel Padilla',
    credentials:
      'First Female Spokesperson · Armed Forces of the Philippines · Signal Corps & Cybersecurity',
    quote:
      'You have to be able to strike that balance between mission accomplishment and welfare of your men.',
    quoteTimestamp: '22:27',
    quoteYoutubeId: 'JNpeGmzPIKw',
    photo:
      'https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/11/03/18/110318a5-d417-ce83-67ec-e8d16b9bf314/mza_15272204601526541129.jpg/600x600bb.jpg',
    bio:
      "Colonel Francel Padilla is the first female spokesperson in the 88-year history of the Armed Forces of the Philippines. A Signal Corps officer and cybersecurity expert, she is one of the pioneering female cadets to complete full military training. Her career spans command and control in high-stakes environments, from cyber and communications (C4S) to data sovereignty and national security strategy.",
    linkedin: 'https://www.linkedin.com/',
    accent: 'from-[#EF5B5B]/25',
    episodes: [
      {
        title: 'The First Female Spokesperson of the AFP on AI, Leadership & Failure',
        slug: 'first-female-afp-spokesperson-ai-leadership',
        cover: 'https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/11/03/18/110318a5-d417-ce83-67ec-e8d16b9bf314/mza_15272204601526541129.jpg/600x600bb.jpg',
      },
    ],
  },
  {
    id: 'floudas',
    name: 'Dr. Demetrius Floudas',
    credentials:
      'Senior Associate · University of Cambridge · AI Governance & National Security Advisor',
    quote:
      'Allowing large artificial intelligence models to be open sourced is the equivalence of allowing nuclear weapons to be sold at the corner shop.',
    quoteTimestamp: '5:55',
    quoteYoutubeId: 'Z9quGBSr-Hw',
    photo:
      'https://is1-ssl.mzstatic.com/image/thumb/Podcasts221/v4/e1/40/99/e1409972-1587-d5a3-1db2-a82a84f8066f/mza_2169470194746689441.jpg/600x600bb.jpg',
    bio:
      "Dr. Demetrius Floudas is a Senior Associate at the University of Cambridge and a trusted advisor to governments, global institutions, and tech leaders on AI governance and regulation. He has spent his career at the forefront of shaping AI policies — balancing innovation with security, tackling open-source AI risks, and mapping AI's impact on labor markets and national defense.",
    linkedin: 'https://www.linkedin.com/',
    accent: 'from-blue-500/25',
    episodes: [
      {
        title: 'The Global Challenge of AI Regulation',
        slug: 'the-global-challenge-of-ai-regulation',
        cover: 'https://is1-ssl.mzstatic.com/image/thumb/Podcasts221/v4/e1/40/99/e1409972-1587-d5a3-1db2-a82a84f8066f/mza_2169470194746689441.jpg/600x600bb.jpg',
      },
    ],
  },
  {
    id: 'spengel',
    name: 'Prof. Dr. Christoph Spengel',
    credentials:
      'Professor of Business Taxation · University of Mannheim · Advisor, European Commission',
    quote:
      'We must be sure that the information provided by the use of AI is reliable and not a fake news.',
    quoteTimestamp: '12:17',
    quoteYoutubeId: 'o1iMR6V0LZQ',
    photo:
      'https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/6d/b7/ee/6db7eef4-b033-6316-a31a-0f9b769d0a98/mza_5652488845725376484.jpg/600x600bb.jpg',
    bio:
      "Professor Dr. Christoph Spengel is a leading authority in business taxation and professor at the University of Mannheim. With decades of experience advising the German Federal Ministry of Finance and the European Commission, he explores how AI is reshaping corporate tax strategy, compliance, and the harmonization of digital economy taxation across borders.",
    linkedin: 'https://www.linkedin.com/',
    accent: 'from-amber-400/25',
    episodes: [
      {
        title: 'Taxing the Future: How AI is Transforming Global Taxation',
        slug: 'taxing-the-future-how-ai-is-transforming-global-taxation',
        cover: 'https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/6d/b7/ee/6db7eef4-b033-6316-a31a-0f9b769d0a98/mza_5652488845725376484.jpg/600x600bb.jpg',
      },
    ],
  },
];

const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };
const REVEAL = { type: 'spring', stiffness: 520, damping: 26, mass: 0.6 };

const FeaturedGuests = () => {
  const [activeGuest, setActiveGuest] = useState(null);

  return (
    <section
      id="guests"
      className="relative py-16 sm:py-24 px-4 sm:px-6"
      data-testid="featured-guests"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#EF5B5B]/5 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
          className="mb-10 sm:mb-14 text-center"
        >
          <span className="inline-block px-4 py-2 rounded-full glass text-xs sm:text-sm text-gray-300 mb-5 tracking-[0.25em] uppercase">
            The Guests
          </span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold">
            Featured <span className="gradient-text">Conversations</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mt-4">
            CEOs, founders, and elite leaders — the calibre of people redefining business and technology.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8"
          data-testid="featured-guests-grid"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.05, delayChildren: 0.02 } },
          }}
        >
          {FEATURED.map((g) => (
            <motion.button
              key={g.id}
              type="button"
              onClick={() => setActiveGuest(g)}
              variants={{
                hidden: { opacity: 0, y: 24, scale: 0.94 },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: REVEAL,
                },
              }}
              whileHover={{ scale: 1.04, y: -6 }}
              whileTap={{ scale: 0.98 }}
              transition={SPRING}
              className="episode-card group text-left glass-strong rounded-3xl overflow-hidden flex flex-col will-change-transform"
              data-testid={`guest-card-${g.id}`}
              aria-label={`Read bio for ${g.name}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${g.accent} via-transparent to-transparent opacity-0 group-hover:opacity-100 smooth pointer-events-none`} />

              <div className="relative block">
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={g.photo}
                    alt={g.name}
                    className="w-full h-full object-cover group-hover:scale-105 smooth"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#EF5B5B] text-white text-[10px] font-bold uppercase tracking-widest">
                    Featured
                  </span>
                </div>
              </div>

              <div className="relative p-6 sm:p-7 flex flex-col flex-1">
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-[#EF5B5B] smooth">
                  {g.name}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-5">
                  {g.credentials}
                </p>

                {g.quote && (
                  <blockquote
                    data-testid={`guest-quote-${g.id}`}
                    className="relative mb-5 pl-4 border-l-2 border-[#EF5B5B]/60 flex-1"
                  >
                    <span aria-hidden className="absolute -left-1.5 -top-3 text-4xl font-serif text-[#EF5B5B]/40 leading-none select-none">
                      “
                    </span>
                    <p className="text-[13px] sm:text-sm text-gray-200 italic leading-relaxed">
                      {g.quote}
                    </p>
                    {(g.quoteTimestamp || g.quoteYoutubeId) && (
                      <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gray-500">
                        {g.quoteTimestamp && (
                          <span>Ep. quote · <span className="text-[#EF5B5B] font-bold">{g.quoteTimestamp}</span></span>
                        )}
                        {g.quoteYoutubeId && (
                          <a
                            href={g.quoteTimestamp
                              ? `https://www.youtube.com/watch?v=${g.quoteYoutubeId}&t=${g.quoteTimestamp.split(':').reverse().reduce((a, v, i) => a + parseInt(v, 10) * [1, 60, 3600][i], 0)}s`
                              : `https://www.youtube.com/watch?v=${g.quoteYoutubeId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`guest-quote-source-${g.id}`}
                            className="text-gray-400 hover:text-[#EF5B5B] smooth"
                          >
                            Watch →
                          </a>
                        )}
                      </div>
                    )}
                  </blockquote>
                )}

                <span
                  data-testid={`guest-bio-cta-${g.id}`}
                  className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-[#EF5B5B] group-hover:gap-3 smooth"
                >
                  Read bio & episodes <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ ...REVEAL, delay: 0.1 }}
          className="mt-12 sm:mt-16 flex justify-center"
        >
          <Link
            to="/episodes"
            data-testid="featured-more-episodes"
            className="group inline-flex items-center gap-2.5 px-8 sm:px-10 py-4 btn-red rounded-full text-base sm:text-lg font-black tracking-wide"
          >
            More Episodes
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 smooth" />
          </Link>
        </motion.div>
      </div>

      <GuestBioModal
        guest={activeGuest}
        open={!!activeGuest}
        onClose={() => setActiveGuest(null)}
      />
    </section>
  );
};

export default FeaturedGuests;
